import React, { useState, useEffect, useRef } from "react";
import {
  fetchTournamentResultTypesList,
  createTournamentResultType,
  updateTournamentResultType,
  deleteTournamentResultType,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";

const TournamentResultTypeMaster = ({ onDirectoryChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ id: null, result_name: "", rank_order: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await fetchTournamentResultTypesList();
      setRows(res?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load result types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSubmit = async () => {
    setFormError(null);
    setSuccessMsg("");
    const name = (formData.result_name ?? "").trim();
    if (!name) {
      setFormError("Result name is required");
      return;
    }
    const rank =
      formData.rank_order !== "" && formData.rank_order != null
        ? Number(formData.rank_order)
        : null;
    if (formData.rank_order !== "" && !Number.isFinite(rank)) {
      setFormError("Rank order must be a number");
      return;
    }
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      if (isEditing) {
        await updateTournamentResultType(
          formData.id,
          { result_name: name, rank_order: rank },
          csrfToken,
        );
        setSuccessMsg("Result type saved.");
      } else {
        await createTournamentResultType({ result_name: name, rank_order: rank }, csrfToken);
        setSuccessMsg("Result type created.");
      }
      setFormData({ id: null, result_name: "", rank_order: "" });
      setIsEditing(false);
      await loadRows();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message || "Save failed");
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const ALERT_ID = "ms-tour-result-type-alert";

  if (loading) return <p className="ub-loading">Loading tournament result types…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>
        Tournament result type master
      </h3>
      <p className="ub-section__hint" style={{ margin: "0 0 1rem", fontSize: "0.875rem", color: "#64748b" }}>
        Labels used when volunteers record outcomes (e.g. Winner, Runner-up). Lower rank order sorts first.
      </p>
      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit result type" : "Add result type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFormData({ id: null, result_name: "", rank_order: "" });
                    setIsEditing(false);
                    setFormError(null);
                  }}
                >
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton type="button" variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save changes" : "Create"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id="trt-name"
            label="Result name"
            value={formData.result_name}
            onChange={(v) => setFormField("result_name", v)}
            required
          />
          <GlobalTextField
            id="trt-rank"
            label="Rank order (optional)"
            value={formData.rank_order}
            onChange={(v) => setFormField("rank_order", v)}
            placeholder="e.g. 1 for Winner"
          />
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Tournament result types"
        headers={[
          { key: "result_name", label: "Name" },
          { key: "rank_order", label: "Rank order" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
      >
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ textAlign: "center", padding: "1.5rem", color: "#64748b" }}>
              No result types yet.
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.id}>
              <td style={{ fontWeight: 600 }}>{r.result_name}</td>
              <td>{r.rank_order ?? "—"}</td>
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  <GlobalTableActionButton
                    action="edit"
                    onClick={() => {
                      setFormData({
                        id: r.id,
                        result_name: r.result_name || "",
                        rank_order: r.rank_order != null ? String(r.rank_order) : "",
                      });
                      setIsEditing(true);
                      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  />
                  <GlobalTableActionButton
                    action="delete"
                    onClick={() => {
                      setItemToDelete(r);
                      setShowDelete(true);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDelete}
        onClose={() => !isDeleting && setShowDelete(false)}
        title="Deactivate result type"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton type="button" onClick={() => setShowDelete(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="danger"
              loading={isDeleting}
              onClick={async () => {
                if (!itemToDelete?.id) return;
                setIsDeleting(true);
                try {
                  let csrfToken = localStorage.getItem("csrf-token");
                  if (!csrfToken) {
                    csrfToken = await getCsrfToken();
                    localStorage.setItem("csrf-token", csrfToken);
                  }
                  await deleteTournamentResultType(itemToDelete.id, csrfToken);
                  setSuccessMsg("Result type deactivated.");
                  setShowDelete(false);
                  setItemToDelete(null);
                  await loadRows();
                  onDirectoryChange?.();
                } catch (err) {
                  setFormError(err.message || "Delete failed");
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              Confirm
            </GlobalButton>
          </div>
        }
      >
        <p>
          Deactivate <b>{itemToDelete?.result_name}</b>? Existing tournament rows keep their label text.
        </p>
      </GlobalModal>
    </div>
  );
};

export default TournamentResultTypeMaster;
